import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect } from "react";
import { createVibe, getCategoriesCount } from "../../api/categories";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Loader from "../../components/Loader";

const Step3Categories = ({
  formData,
  setFormData,
  goNext,
  goBack,
  skipToStep,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAddressPopup, setShowAddressPopup] = useState(false);
  const [manualAddress, setManualAddress] = useState("");
  const [saving, setSaving] = useState(false);

  // Distance labels
  const distances = [
    t("distance_3_miles", "3 Miles"),
    t("distance_10_miles", "10 Miles"),
    t("distance_20_miles", "20 Miles"),
    t("distance_50_miles", "50 Miles"),
    t("distance_100_miles", "100 Miles"),
  ];

  // Maps distance labels to miles
  const distanceToMiles = {
    [t("distance_3_miles", "3 Miles").toLowerCase()]: 3,
    [t("distance_10_miles", "10 Miles").toLowerCase()]: 10,
    [t("distance_20_miles", "20 Miles").toLowerCase()]: 20,
    [t("distance_50_miles", "50 Miles").toLowerCase()]: 50,
    [t("distance_100_miles", "100 Miles").toLowerCase()]: 100,
  };

  // Maps distance labels to radius (meters)
  const distanceToRadius = {
    [t("distance_3_miles", "3 Miles").toLowerCase()]: 4800,
    [t("distance_10_miles", "10 Miles").toLowerCase()]: 16000,
    [t("distance_20_miles", "20 Miles").toLowerCase()]: 32000,
    [t("distance_50_miles", "50 Miles").toLowerCase()]: 80000,
    [t("distance_100_miles", "100 Miles").toLowerCase()]: 160000,
  };

  // 🔥 Fetch categories + pass miles/lat/lng
  useEffect(() => {
    const fetchCategories = async () => {
      const payload = {
        miles: formData?.miles || null,
        latitude: formData?.latitude || null,
        longitude: formData?.longitude || null,
      };

      try {
        const res = await getCategoriesCount(payload);
        setCategories(res?.data?.data || []);
      } catch (err) {
        setCategories([]);
      }
    };

    fetchCategories();
  }, [formData.miles, formData.latitude, formData.longitude]);

  // Handle distance selection
  const handleDistanceSelect = (dist) => {
    const miles = distanceToMiles[dist.toLowerCase()] || null;
    const radius = distanceToRadius[dist.toLowerCase()] || 50000;

    setFormData((prev) => ({
      ...prev,
      distance: dist,
      miles,
      radius,
    }));
  };

  // Category select
  const handleCategorySelect = (cat) => {
    setSelectedCategory(cat);

    setFormData((prev) => ({
      ...prev,
      category: cat.name,
      type: cat.type,
      selectedCategory: cat,
      category_id: cat.id,
    }));
  };

  // Save manual address
  const saveAddress = async () => {
    if (!manualAddress.trim()) {
      toast.error(t("please_enter_an_address"));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      address: manualAddress,
      selectedPlace: null,
    }));

    setShowAddressPopup(false);

    if (skipToStep) {
      setFormData((prev) => ({
        ...prev,
        selectedPlace: "",
        date: "",
        time: "",
      }));
      skipToStep(5);
    }
  };

  // Save category
  const handleSaveCategory = async () => {
    if (!formData.selectedCategory) {
      toast.error(t("please_select_miles_and_category"));
      return;
    }

    if (!formData.distance) {
      toast.error(t("please_select_distance"));
      return;
    }

    setSaving(true);

    const payload = {
      id: formData.id,
      vibe_category_id: formData.vibe_category_id,
      category_id: formData.selectedCategory?.id,
      steps: 3,
    };

    try {
      const res = await createVibe(payload);

      if (res && res.data) {
        setFormData((prev) => ({
          ...prev,
          category_id: formData.selectedCategory?.id,
          data: "",
          time: "",
          selectedPlace: "",
        }));

        goNext();
      } else {
        toast.error(t("failed_to_save_category"));
      }
    } catch (err) {
      toast.error(t("failed_to_save_category"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => {
            location.state = {};
            goBack();
          }}
          className="py-2 rounded-lg font-semibold text-gray-700"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>

        <h1 className="text-md md:text-lg font-bold" style={{ color: "#6d2af6" }}>
          {t("vibe")}
        </h1>

        <button
          className="text-sm px-4 py-2 rounded-lg font-semibold bg-[#6d29f6] text-white flex justify-center items-center"
          onClick={handleSaveCategory}
        >
          {saving ? <Loader loaderColor="white" size={20} /> : t("save_next")}
        </button>
      </div>

      <h1 className="flex w-full justify-center py-3 text-black dark:text-white text-md md:text-lg font-bold">
        {t("categories")}
      </h1>

      {/* DISTANCE BUTTONS */}
      <div className="flex overflow-x-auto gap-2 mb-6 pb-2 hide-scrollbar">
        {distances.map((dist, idx) => (
          <button
            key={idx}
            onClick={() => handleDistanceSelect(dist)}
            className={`px-4 py-2 rounded-lg border-2 font-medium whitespace-nowrap ${
              formData.distance === dist
                ? "text-white border-[#6d2af6] bg-[#6d2af6]"
                : "border-gray-300 bg-[#c4cee0] dark:bg-[#C5CEE0] dark:text-black"
            }`}
            style={{ minWidth: 100 }}
          >
            {dist}
          </button>
        ))}
      </div>

      <p className="text-sm text-black dark:text-white text-center mb-2">
        {t("enter_address_optional_or_choose_category")}
      </p>

      <div className="flex justify-center mb-2">
        <button
          onClick={() => setShowAddressPopup(true)}
          className="text-sm w-full max-w-xs py-3 rounded-xl font-semibold text-lg"
          style={{ background: "#6d2af6", color: "#fff" }}
        >
          {t("enter_address")}
        </button>
      </div>

      <p className="text-gray-500 text-center mb-4">{t("or")}</p>

      {/* ADDRESS POPUP */}
      {showAddressPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white dark:bg-[#252739] p-6 rounded-lg w-96">
            <h2 className="text-lg font-semibold mb-4">{t("enter_address")}</h2>

            <input
              type="text"
              value={manualAddress}
              onChange={(e) => setManualAddress(e.target.value)}
              className="w-full border rounded-lg p-2 mb-4 dark:bg-[#252739] dark:text-white"
              placeholder={t("type_your_address")}
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowAddressPopup(false)}
                className="px-4 py-2 rounded-lg bg-[#e85a59] text-white"
              >
                {t("cancel")}
              </button>
              <button
                onClick={saveAddress}
                className="px-4 py-2 rounded-lg text-white"
                style={{ background: "#6d2af6" }}
              >
                {t("save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CATEGORY LIST */}
      <div className="flex flex-col gap-3">
        {categories.map((cat, idx) => (
          <div
            key={cat.id || idx}
            onClick={() => handleCategorySelect(cat)}
            className={`relative px-4 py-3 rounded-xl border-2 cursor-pointer text-lg font-semibold flex items-center justify-center ${
              formData.category === cat.name
                ? "text-white border-[#6d2af6] bg-[#6d2af6]"
                : "border-[#6d29f6] bg-white dark:bg-white dark:text-black"
            }`}
          >
            <span className="text-sm">{cat.name}</span>

            <span
              className="ml-2 absolute top-[-8px] right-[-10px] bg-[#6d29f6] text-white text-sm w-7 h-7 flex items-center justify-center rounded-full border-2 border-white shadow"
              style={{ fontWeight: "bold" }}
            >
              {cat.count}
            </span>
          </div>
        ))}
      </div>
    </>
  );
};

export default Step3Categories;
